import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animated, useTransition } from '@react-spring/web';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { RootState } from 'store';
import {
    clearSnackbar,
    ColorThemes,
    menuBarAlwaysOn,
    mobileModeOff,
    mobileModeOn,
    setSize,
    setTheme,
    toggleMenuBar,
} from 'store/features/ui';
import MainContent from 'components/app/MainContent/MainContent';
import MainMenuBar from 'components/app/MainMenuBar/MainMenuBar';
import TopStatusBar from 'components/app/TopStatusBar/TopStatusBar';
import styles from './App.module.scss';
import { Alert, Portal, Snackbar } from '@mui/material';
import ZendeskWrapper from 'components/app/ZendeskWrapper/ZendeskWrapper';
import { Theme, ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useUserSession } from 'hooks';
import {
    darkTheme,
    legacyLightTheme,
    legacyDarkTheme,
    lightTheme,
} from 'lib/themes';

export default function App() {
    const location = useLocation();
    const ui = useSelector((state: RootState) => state.ui);
    const dispatch = useDispatch();

    const handleWindowStateChange = useCallback(() => {
        dispatch(
            setSize({
                width: window.innerWidth,
                height: window.innerHeight,
            })
        );
        if (window.innerWidth > 750) {
            dispatch(mobileModeOff());
            dispatch(menuBarAlwaysOn());
            return;
        }
        dispatch(mobileModeOn());
    }, [dispatch]);

    const mainViewClassNames = useCallback(() => {
        if (location.pathname === '/') return styles['landing-view'];
        if (ui.animatingViewTransition)
            return classNames(styles['main-view'], styles['animating']);
        return styles['main-view'];
    }, [location.pathname, ui.animatingViewTransition]);

    const menuChanged = () => {
        if (!ui.isMobile) return;
        dispatch(toggleMenuBar());
    };
    const menuBarTransition = useTransition(ui.menuBarActive, {
        from: {
            transform: ui.isMobile
                ? 'translate3d(-100%, 0, 0)'
                : 'translate3d(0%, 0, 0)',
        },
        enter: { transform: 'translate3d(0%, 0, 0)' },
        leave: { transform: 'translate3d(-100%, 0, 0)' },
    });

    useEffect(() => {
        window.addEventListener('resize', handleWindowStateChange);
        handleWindowStateChange();
        return () => {
            window.removeEventListener('resize', handleWindowStateChange);
        };
    }, [handleWindowStateChange]);

    /** Get mui theme object from the theme name. */
    const getTheme = (theme: ColorThemes): Theme => {
        if (theme === 'dark') {
            return darkTheme;
        } else if (theme === 'legacyLight') {
            return legacyLightTheme;
        } else if (theme === 'legacyDark') {
            return legacyDarkTheme;
        } else {
            // Default to returning light theme
            return lightTheme;
        }
    };

    // Media query to check if theme preference is set on user's OS.
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    // Initialize color theme based on user localStorage data. If unavailable, default to user preference based on OS settings.
    const [currentTheme, setCurrentTheme] = useState<Theme>(
        getTheme(
            localStorage.getItem('colorTheme')
                ? (localStorage.getItem('colorTheme') as ColorThemes)
                : prefersDarkMode
                ? 'dark'
                : 'light'
        )
    );

    const session = useUserSession();

    // Update UI slice theme state based on user session.
    useEffect(() => {
        if (!session) return;
        // If the user has a session entry for theme, update the UI.theme state to reflect that value.
        if (session?.userPreferences?.theme) {
            dispatch(setTheme(session.userPreferences.theme));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, session?.userPreferences?.theme, prefersDarkMode, session]);

    // Handle changes to color theme based on user changes on profile preferences page.
    useEffect(() => {
        if (ui.theme === 'dark') {
            setCurrentTheme(darkTheme);
        } else if (ui.theme === 'light') {
            setCurrentTheme(lightTheme);
        } else if (ui.theme === 'legacyLight') {
            setCurrentTheme(legacyLightTheme);
        } else if (ui.theme === 'legacyDark') {
            setCurrentTheme(legacyDarkTheme);
        } else {
            // Default to light theme
            setCurrentTheme(lightTheme);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ui.theme]);

    return (
        <div className={styles.content} data-testid="App">
            <ThemeProvider theme={currentTheme}>
                <CssBaseline enableColorScheme />
                {location.pathname !== '/' && (
                    <TopStatusBar onMenuToggle={menuChanged} />
                )}
                {menuBarTransition((props, shouldShow) => (
                    <div>
                        {location.pathname !== '/' && !!shouldShow && (
                            <animated.div style={props}>
                                <MainMenuBar onMenuToggle={menuChanged} />
                            </animated.div>
                        )}
                    </div>
                ))}
                <div className={mainViewClassNames()}>
                    <MainContent />
                </div>
                <ZendeskWrapper />
                <Portal>
                    <Snackbar
                        open={ui.snackbar.isActive}
                        autoHideDuration={
                            ui.snackbar.alertIsSticky ? null : 5000
                        }
                        onClose={() => dispatch(clearSnackbar())}
                        onClick={() => dispatch(clearSnackbar())}
                    >
                        <Alert severity={ui.snackbar.severity}>
                            {ui.snackbar.message}
                        </Alert>
                    </Snackbar>
                </Portal>
            </ThemeProvider>
        </div>
    );
}
